import { Utils } from '@common';
import { React, Styled } from '@common/3rd';
import { WidgetContext } from '@component';
import { ModelContainerWidget } from '@component/model-widget';
import { Modeller } from '@model';
import {
	ArrayContainerComponents,
	ArrayContainerNoDataComponents,
	ArrayContainerNoDataProps,
	ArrayContainerProps,
	ArrayContainerRowComponents,
	ArrayContainerRowProps,
	ArrayContainerRowsProps
} from '@my-types';
import { FlexContainer } from './container-abstract';

class ArrayContainerNoData<
	P extends ArrayContainerNoDataProps,
	S,
	C extends ArrayContainerNoDataComponents
> extends FlexContainer<P, S, C> {
	protected initComponents() {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'array-container-nodata'
			})`
				display: flex;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.getLabel()}
				{this.renderChildren()}
			</C.Box>
		);
	}
	getLabel() {
		if (this.props.label === false) {
			return null;
		} else {
			return this.props.label || '当前没有数据需要显示.';
		}
	}
}

class ArrayContainerRow<
	P extends ArrayContainerRowProps,
	S,
	C extends ArrayContainerRowComponents
> extends FlexContainer<P, S, C> {
	protected initComponents() {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'array-container-row'
			})`
				display: flex;
				> * {
					flex-grow: 1;
				}
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			<WidgetContext.Consumer>
				{value => {
					const newValue = Object.assign({}, value, {
						rowIndex: this.props.index
					});
					return (
						<WidgetContext.Provider value={newValue}>
							<C.Box>{this.renderChildren()}</C.Box>
						</WidgetContext.Provider>
					);
				}}
			</WidgetContext.Consumer>
		);
	}
}

/**
 * 行代理, 任务是循环描画行, 并且将每行的内容传给行组件
 */
class ArrayContainerRows<P extends ArrayContainerRowsProps, S, C> extends ModelContainerWidget<P, S, C> {
	static getConcernedChildren() {
		return {
			nodata: 'array.nodata'
		};
	}
	protected initComponents(): C {
		return {} as C;
	}
	protected isNoData(child: any): boolean {
		return (
			child.type.name === 'ArrayContainerNoData' || (child.type.__proto__ || {}).name === 'ArrayContainerNoData'
		);
	}
	protected renderNoData(): any {
		return React.Children.toArray(this.props.children).filter((child: any) => this.isNoData(child));
	}
	protected renderDataRow(rowIndex: number): any {
		return React.Children.toArray(this.props.children).filter((child: any) => !this.isNoData(child));
	}
	/**
	 * 数组嵌套
	 */
	protected renderChildren(): any {
		const values = Utils.toArray(this.getValueFromModel());
		if (values.length === 0) {
			if (this.shouldRenderNoData()) {
				return this.renderNoData();
			}
		} else {
			const props = this.props;
			return Utils.toArray(this.getValueFromModel())
				.sort(this.getSorter())
				.map((row, rowIndex) => {
					return (
						<ArrayContainerRow
							{...props}
							key={rowIndex}
							model={Modeller.asProxied(row)}
							arrayHolder={this.getModel()}
							index={rowIndex}
						>
							{this.renderDataRow(rowIndex)}
						</ArrayContainerRow>
					);
				});
		}
	}
	render() {
		return <React.Fragment>{this.renderChildren()}</React.Fragment>;
	}
	private shouldRenderNoData(): boolean {
		return this.props.nodata != null;
	}
	protected getSorter() {
		return this.props.sort || (() => 0);
	}
}

class ArrayContainer<P extends ArrayContainerProps, S, C extends ArrayContainerComponents> extends FlexContainer<
	P,
	S,
	C
> {
	boxRef: React.RefObject<any>;

	static getConcernedChildren() {
		return {
			row: 'array.rows'
		};
	}
	constructor(props: any, context?: any) {
		super(props, context);
		this.boxRef = React.createRef();
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'array-container'
			})`
				display: flex;
				flex-direction: column;
				${props => this.renderCSS(props.theme)}
			`
		} as C;
	}
	render() {
		const C = this.getComponents();
		return <C.Box ref={this.boxRef}>{this.renderChildren()}</C.Box>;
	}
}

export { ArrayContainer, ArrayContainerRows, ArrayContainerRow, ArrayContainerNoData };
